// src/pages/Readings.js
import React, { useMemo, useEffect, useState } from "react";
import { sanity, urlFor } from "../sanityClient";

// topics/tabs you want to show
const TOPICS = ["Biking", "Events", "Policy", "Community", "Rambles"];

// --- GROQ query ---
// This matches Sanity’s default blog template pretty well:
// - title
// - publishedAt
// - author reference -> name
// - categories[] references -> title
// - mainImage (some templates use mainImage; some use coverImage)
const POSTS_QUERY = `*[_type == "post" && defined(slug.current)]
| order(publishedAt desc) {
  _id,
  title,
  "slug": slug.current,
  publishedAt,
  excerpt,
  "authors": coalesce(author->name, "Unknown"),
  "topics": categories[]->title,
  mainImage,
  coverImage,
  tags
}`;

function isInternalHref(href) {
  return typeof href === "string" && href.startsWith("/#/");
}

function fmtYear(iso) {
  try {
    return new Date(iso).getFullYear();
  } catch {
    return "";
  }
}

export default function Readings() {
  const [query, setQuery] = useState("");
  const [activeTopic, setActiveTopic] = useState("All");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    sanity
      .fetch(POSTS_QUERY)
      .then((data) => {
        if (!alive) return;
        setPosts(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Sanity fetch failed:", err);
        if (!alive) return;
        setPosts([]);
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  // Convert Sanity docs to the same shape your UI expects
  const READINGS = useMemo(() => {
    return posts.map((p) => {
      const topic =
        (Array.isArray(p.topics) && p.topics[0]) ||
        (Array.isArray(p.tags) && p.tags[0]) ||
        "Rambles";

      // Prefer coverImage if you used it in your custom schema, else mainImage
      const img = p.coverImage || p.mainImage;

      return {
        title: p.title,
        authors: p.authors,
        year: p.publishedAt ? fmtYear(p.publishedAt) : "",
        source: topic, // you can rename this label later if you want
        url: `/#/post/${p.slug}`, // internal link to your post page
        topic,
        tags: Array.isArray(p.tags) ? p.tags : [],
        blurb: p.excerpt || "",
        thumb: img ? urlFor(img).width(480).height(360).fit("crop").url() : null
      };
    });
  }, [posts]);

  const topicsWithAll = useMemo(
    () => ["All", ...TOPICS.filter((t) => READINGS.some((r) => r.topic === t))],
    [READINGS]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return READINGS.filter((r) => {
      const matchesTopic = activeTopic === "All" || r.topic === activeTopic;
      if (!q) return matchesTopic;

      const hay = [r.title, r.authors, r.source, r.topic, ...(r.tags || [])]
        .join(" ")
        .toLowerCase();

      return matchesTopic && hay.includes(q);
    }).sort((a, b) => a.title.localeCompare(b.title));
  }, [query, activeTopic, READINGS]);

  return (
    <div
      className="readings-root"
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(1200px 600px at 20% -10%, rgba(255,255,255,0.70), rgba(255,255,255,0.0)), linear-gradient(180deg, #ECEBFF 0%, #E3E1FF 45%, #EEEAFE 100%)"
      }}
    >
      <header
        className="readings-header"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          backdropFilter: "blur(10px)",
          background: "rgba(236,235,255,0.72)",
          borderBottom: "1px solid rgba(99,102,241,0.20)"
        }}
      >
        <div style={{ maxWidth: 980, margin: "0 auto", padding: "16px 20px" }}>
          <h1 style={{ margin: 0, fontSize: 28, color: "#1f1b4d" }}>
            Articles
          </h1>
          <p style={{ margin: "6px 0 0", color: "#3c3a6a" }}>
            Daily musings (published from Sanity)
          </p>
        </div>
      </header>

      <main style={{ maxWidth: 980, margin: "0 auto", padding: "20px" }}>
        {/* Controls */}
        <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
          <div role="tablist" aria-label="Topics" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {topicsWithAll.map((t) => (
              <button
                key={t}
                role="tab"
                aria-selected={activeTopic === t}
                onClick={() => setActiveTopic(t)}
                style={{
                  padding: "7px 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(99,102,241,0.28)",
                  background: activeTopic === t ? "rgba(99,102,241,0.92)" : "rgba(255,255,255,0.75)",
                  color: activeTopic === t ? "#fff" : "#1f1b4d",
                  cursor: "pointer"
                }}
              >
                {t}
              </button>
            ))}
          </div>

          <label style={{ display: "block" }}>
            <span className="sr-only">Search articles</span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title, author, tag…"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(99,102,241,0.22)",
                fontSize: 14,
                background: "rgba(255,255,255,0.80)",
                outline: "none"
              }}
            />
          </label>
        </div>

        {/* Results status */}
        <div aria-live="polite" style={{ marginBottom: 8, color: "#3c3a6a", fontSize: 14 }}>
          {loading ? "Loading…" : `${filtered.length} result${filtered.length === 1 ? "" : "s"}`}
          {activeTopic !== "All" ? ` in “${activeTopic}”` : ""}.
        </div>

        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }}>
          {filtered.map((r, i) => {
            const internal = isInternalHref(r.url);

            return (
              <li key={`${r.title}-${i}`}>
                <article
                  className="reading-card"
                  style={{
                    border: "1px solid rgba(99,102,241,0.22)",
                    borderRadius: 16,
                    padding: 14,
                    background: "rgba(255,255,255,0.78)",
                    transition: "transform 120ms ease"
                  }}
                >
                  <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 14, alignItems: "start" }}>
                    {/* Left image */}
                    <div
                      style={{
                        width: 120,
                        height: 96,
                        borderRadius: 14,
                        overflow: "hidden",
                        border: "1px solid rgba(99,102,241,0.18)",
                        background: "rgba(99,102,241,0.08)"
                      }}
                    >
                      {r.thumb ? (
                        <img
                          src={r.thumb}
                          alt={r.title}
                          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                      ) : null}
                    </div>

                    {/* Right content */}
                    <div>
                      <h3 style={{ margin: "0 0 6px", fontSize: 18, lineHeight: 1.3 }}>
                        <a
                          href={r.url}
                          target={internal ? undefined : "_blank"}
                          rel={internal ? undefined : "noopener noreferrer"}
                          style={{ color: "#3f3ccf", textDecoration: "none" }}
                        >
                          {r.title}
                        </a>
                      </h3>

                      <p style={{ margin: "0 0 6px", color: "#4a4877", fontSize: 14 }}>
                        <strong>{r.source}</strong>
                        {r.year ? ` · ${r.year}` : ""} — {r.authors}
                      </p>

                      {r.blurb && <p style={{ margin: "0 0 10px", color: "#1f1b4d" }}>{r.blurb}</p>}

                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <span style={chipStyle}>{r.topic}</span>
                        {(r.tags || []).map((t) => (
                          <span key={t} style={chipStyle}>{t}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              </li>
            );
          })}
        </ul>

        <style>{`
          .reading-card:hover { transform: translateY(-1px); box-shadow: 0 10px 30px rgba(31,27,77,0.10); }
          .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
          @media (max-width: 560px) {
            .reading-card > div { grid-template-columns: 92px 1fr !important; }
          }
        `}</style>
      </main>
    </div>
  );
}

const chipStyle = {
  display: "inline-block",
  padding: "3px 9px",
  borderRadius: 999,
  border: "1px solid rgba(99,102,241,0.20)",
  fontSize: 12,
  color: "#2e2a63",
  background: "rgba(99,102,241,0.08)"
};